import React, { useEffect, useRef, useState } from "react";
import { listPassages, getPassage, submitSprint, sprintLeaderboard, API } from "@/lib/api";
import Modal from "./Modal";

const REC_MODES = [
  { key: "none",  label: "📵 No recording",   desc: "Just race the clock" },
  { key: "audio", label: "🎤 Record audio",   desc: "Listen back to yourself" },
  { key: "video", label: "🎥 Record video",   desc: "Watch yourself read" },
];

function pickMime(video) {
  const list = video
    ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const m of list) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export default function SpeedReaderModal({ open, onClose, handle }) {
  const [phase, setPhase] = useState("pick");
  const [recMode, setRecMode] = useState("none");
  const [passages, setPassages] = useState([]);
  const [passage, setPassage] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [start, setStart] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState([]);
  const [recError, setRecError] = useState("");
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordedType, setRecordedType] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [verifyStatus, setVerifyStatus] = useState(null); // null | 'uploading' | 'verified' | 'flagged'
  const [verifyFlags, setVerifyFlags] = useState([]);
  const [isRec, setIsRec] = useState(false);
  const [permState, setPermState] = useState("idle"); // idle | testing | granted | denied
  const recStartRef = useRef(0);

  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const livePreviewRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    resetAll();
    listPassages().then(setPassages).catch(() => {});
    sprintLeaderboard(10).then(setBoard).catch(() => {});
  }, [open]);

  useEffect(() => () => cleanupRecording(), []);

  // Re-attach camera stream to whichever live-preview element is currently mounted (countdown vs reading)
  useEffect(() => {
    if (recMode === "video" && streamRef.current && livePreviewRef.current) {
      if (livePreviewRef.current.srcObject !== streamRef.current) {
        livePreviewRef.current.srcObject = streamRef.current;
        livePreviewRef.current.play().catch(() => {});
      }
    }
  }, [phase, recMode]);

  const resetAll = () => {
    setPhase("pick"); setPassage(null); setResult(null); setElapsed(0);
    setRecError(""); cleanupRecording();
  };

  const cleanupRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setIsRec(false);
  };

  const startRecording = async () => {
    if (recMode === "none") return true;
    if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setRecError("This browser doesn't support recording. Try Chrome, Edge, or Firefox.");
      return false;
    }
    if (typeof MediaRecorder === "undefined") {
      setRecError("Your browser can't record media. Update to the latest Chrome/Edge/Firefox.");
      return false;
    }
    try {
      const constraints = recMode === "video"
        ? { audio: true, video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (recMode === "video" && livePreviewRef.current) {
        livePreviewRef.current.srcObject = stream;
        livePreviewRef.current.play().catch(() => {});
      }
      const mime = pickMime(recMode === "video");
      let rec;
      try {
        rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      } catch (codecErr) {
        // Fallback: let the browser pick its default container/codec.
        try { rec = new MediaRecorder(stream); }
        catch (finalErr) {
          setRecError(`Recorder unsupported: ${finalErr.message || "no compatible codec"}.`);
          stream.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          return false;
        }
      }
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onerror = (e) => {
        setRecError(`Recorder error: ${e.error?.name || "unknown"}. Recording stopped.`);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || (recMode === "video" ? "video/webm" : "audio/webm") });
        const url = URL.createObjectURL(blob);
        const duration = (performance.now() - recStartRef.current) / 1000;
        setRecordedUrl(url);
        setRecordedType(recMode);
        setRecordedBlob(blob);
        setRecordedDuration(duration);
      };
      recorderRef.current = rec;
      recStartRef.current = performance.now();
      rec.start(500); // 500ms chunks → quicker first dataavailable, safer for short reads
      setIsRec(true);
      setPermState("granted");
      setRecError("");
      return true;
    } catch (e) {
      const name = e.name || "";
      let msg;
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = `🚫 ${recMode === "video" ? "Camera & mic" : "Microphone"} permission was blocked. Click the 🔒 icon in your address bar → allow ${recMode === "video" ? "camera + microphone" : "microphone"} → reload.`;
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        msg = `🔌 No ${recMode === "video" ? "camera/mic" : "microphone"} detected on this device.`;
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        msg = `⚠ Another app is using the ${recMode === "video" ? "camera or mic" : "microphone"}. Close Zoom/Meet/OBS and try again.`;
      } else if (name === "OverconstrainedError") {
        msg = `📷 Your camera doesn't support the requested settings. Try audio-only.`;
      } else {
        msg = `Couldn't access ${recMode === "video" ? "camera/mic" : "mic"}: ${e.message || name || "unknown"}`;
      }
      setRecError(msg);
      setPermState("denied");
      return false;
    }
  };

  // Quick standalone permission test — lets the user pre-grant before the timer starts.
  const testPermission = async () => {
    if (recMode === "none") return;
    setPermState("testing");
    setRecError("");
    try {
      const constraints = recMode === "video"
        ? { audio: true, video: { facingMode: "user" } }
        : { audio: true };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      s.getTracks().forEach((t) => t.stop());
      setPermState("granted");
    } catch (e) {
      setPermState("denied");
      const name = e.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setRecError(`🚫 Permission blocked. Click the 🔒 icon in your address bar → allow ${recMode === "video" ? "camera + microphone" : "microphone"} → reload.`);
      } else if (name === "NotFoundError") {
        setRecError(`🔌 No ${recMode === "video" ? "camera/mic" : "microphone"} detected.`);
      } else {
        setRecError(`Test failed: ${e.message || name || "unknown error"}`);
      }
    }
  };

  const choose = async (id) => {
    const p = await getPassage(id);
    setPassage(p);
    setPhase("countdown");
    setCountdown(3);
    // Give React a paint cycle so the countdown UI (with the <video> element) mounts before we attach the stream.
    await new Promise((r) => requestAnimationFrame(() => r()));
    // start recording during the countdown so it's warmed up
    if (recMode !== "none") {
      const ok = await startRecording();
      if (!ok) { setPhase("pick"); return; }
    }
    const cdInt = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(cdInt);
          const t0 = performance.now();
          setStart(t0);
          setPhase("reading");
          timerRef.current = setInterval(() => setElapsed((performance.now() - t0) / 1000), 90);
          return 0;
        }
        return c - 1;
      });
    }, 900);
  };

  const finish = async () => {
    clearInterval(timerRef.current);
    const seconds = (performance.now() - start) / 1000;
    setElapsed(seconds);
    // stop recorder, keep stream off
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRec(false);

    setPhase("done");
    const baseResult = { wpm: Math.round((passage.words / seconds) * 60), seconds, words: passage.words };
    if (!handle) { setResult(baseResult); return; }
    try {
      const s = await submitSprint({ scientist_handle: handle, passage_id: passage.id, words: passage.words, seconds });
      setResult(s);
      // upload recording if any — small delay so blob is finalized via onstop
      if (recMode !== "none") {
        setTimeout(() => uploadRecording(s.id, seconds), 350);
      }
      sprintLeaderboard(10).then(setBoard).catch(() => {});
    } catch {
      setResult(baseResult);
    }
  };

  const uploadRecording = async (sprintId, sprintSeconds) => {
    if (!recordedBlob && chunksRef.current.length === 0) return;
    const blob = recordedBlob || new Blob(chunksRef.current, { type: recMode === "video" ? "video/webm" : "audio/webm" });
    const duration = recordedDuration || sprintSeconds;
    setVerifyStatus("uploading");
    const fd = new FormData();
    fd.append("file", blob, `sprint-${sprintId}.webm`);
    fd.append("kind", recMode);
    fd.append("audio_seconds", String(duration));
    try {
      const res = await fetch(`${API}/sprints/${sprintId}/recording`, { method: "POST", body: fd });
      const data = await res.json();
      setVerifyFlags(data.flags || []);
      setVerifyStatus(data.verified ? "verified" : "flagged");
      sprintLeaderboard(10).then(setBoard).catch(() => {});
    } catch {
      setVerifyStatus("flagged");
      setVerifyFlags(["upload failed — try again"]);
    }
  };

  const reset = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null); setRecordedType(null); setRecordedBlob(null); setRecordedDuration(0);
    setVerifyStatus(null); setVerifyFlags([]);
    resetAll();
  };

  return (
    <Modal open={open} onClose={() => { cleanupRecording(); onClose(); }} title="⚡ Speed Reader Race" testId="sprint-modal" wide tint="sun">
      <p className="text-[var(--ink-soft)] text-sm mb-4">
        Read fast, read well. We measure your <strong>Words-Per-Minute (WPM)</strong>. Want to hear yourself? Record audio or video while you read.
      </p>

      {phase === "pick" && (
        <div data-testid="sprint-pick">
          {/* Recording mode selector */}
          <div className="eyebrow text-[var(--orange)] mb-2">// recording (optional)</div>
          <div className="grid sm:grid-cols-3 gap-2.5 mb-3">
            {REC_MODES.map((m) => {
              const sel = recMode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { setRecMode(m.key); setRecError(""); setPermState("idle"); }}
                  data-testid={`rec-mode-${m.key}`}
                  className="card-flat text-left"
                  style={{
                    background: sel ? "var(--sun)" : "#fff",
                    borderColor: "var(--ink)",
                    boxShadow: sel ? "4px 4px 0 0 var(--ink)" : "2px 2px 0 0 var(--ink)",
                  }}
                >
                  <div className="font-fun text-base text-[var(--ink)]">{m.label}</div>
                  <div className="label-tag mt-0.5">{m.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Pre-flight permission test + error banner */}
          {recMode !== "none" && (
            <div className="card-flat tint-cream mb-5" data-testid="rec-preflight">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-[var(--ink)]">
                  <strong>Before you race:</strong> grant {recMode === "video" ? "camera + mic" : "microphone"} access so your read counts.
                </div>
                <div className="flex items-center gap-2">
                  {permState === "granted" && <span className="pill pill-green" data-testid="perm-ok">✅ ready</span>}
                  {permState === "denied" && <span className="pill pill-red">⚠ blocked</span>}
                  <button onClick={testPermission} disabled={permState === "testing"} className="btn-ghost text-sm" data-testid="rec-test">
                    {permState === "testing" ? "🧪 testing…" : "🎙 Test mic/cam"}
                  </button>
                </div>
              </div>
              {recError && (
                <div className="mt-2 text-sm font-bold text-[var(--red)]" data-testid="rec-error">{recError}</div>
              )}
            </div>
          )}

          <div className="eyebrow mb-2 text-[var(--orange)]">// pick a passage</div>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            {passages.map((p) => (
              <button key={p.id} onClick={() => choose(p.id)} data-testid={`sprint-passage-${p.id}`} className="card card-hover text-left">
                <div className="text-3xl mb-1">{p.level === "easy" ? "🌱" : p.level === "average" ? "🚀" : p.level === "genius" ? "🧠" : "🔥"}</div>
                <div className="font-fun text-lg text-[var(--ink)]">{p.title}</div>
                <div className="label-tag mt-1">{p.words} words · {p.level}</div>
              </button>
            ))}
          </div>

          <div className="card tint-cream">
            <div className="font-fun text-lg text-[var(--ink)] mb-2">🏆 Top Speed Readers</div>
            <div className="space-y-1.5">
              {board.length === 0 && <div className="text-[var(--ink-soft)] text-sm">Be the first! No sprints yet.</div>}
              {board.map((r, i) => (
                <div key={r.handle} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="pill" style={{ background: i === 0 ? "var(--sun)" : i === 1 ? "#e5e7eb" : i === 2 ? "#fdba74" : "#fff" }}>{i + 1}</span>
                    <span className="font-fun text-[var(--ink)] truncate">{r.scientist_name}</span>
                    <span className="label-tag truncate">@{r.handle}</span>
                    {r.verified && <span className="pill pill-green text-[0.6rem]" title="Verified with recording">✅</span>}
                    {r.has_recording && !r.verified && <span className="pill pill-red text-[0.6rem]" title="Recording flagged">⚠</span>}
                  </div>
                  <span className="stat-num text-xl text-[var(--red)]">{r.best_wpm} WPM</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {phase === "countdown" && (
        <div className="text-center py-10" data-testid="sprint-countdown">
          <div className="eyebrow text-[var(--orange)] mb-3">// get ready, scientist</div>
          {recMode === "video" && (
            <video ref={livePreviewRef} muted playsInline className="mx-auto rounded-2xl mb-3" style={{ width: 280, border: "2.5px solid var(--ink)", boxShadow: "4px 4px 0 0 var(--ink)" }} />
          )}
          <div className="stat-num text-[8rem] sm:text-[10rem] text-[var(--red)] leading-none animate-tick">{countdown}</div>
          <div className="font-fun text-2xl text-[var(--ink)] mt-2">Reading: <em>{passage?.title}</em></div>
          {isRec && <div className="mt-2 pill pill-red animate-pulse inline-flex">🔴 REC starting</div>}
          {recError && <div className="mt-3 text-[var(--red)] font-bold">{recError}</div>}
        </div>
      )}

      {phase === "reading" && passage && (
        <div data-testid="sprint-reading">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="pill pill-red animate-tick text-base" style={{ padding: "0.45rem 1rem" }} data-testid="sprint-timer">⏱ {elapsed.toFixed(1)}s</div>
              {isRec && (
                <div className="pill pill-red animate-pulse" data-testid="rec-indicator">
                  🔴 REC {recMode === "video" ? "video" : "audio"}
                </div>
              )}
            </div>
            <div className="pill pill-sun">{passage.words} words</div>
          </div>
          {recMode === "video" && (
            <video
              ref={livePreviewRef}
              muted
              playsInline
              autoPlay
              className="rounded-xl mb-3 mx-auto block"
              style={{ width: 200, border: "2px solid var(--ink)", boxShadow: "3px 3px 0 0 var(--ink)" }}
              data-testid="live-video"
            />
          )}
          {recMode === "audio" && isRec && (
            <div className="flex justify-center items-center gap-2 mb-3 text-[var(--red)] font-mono text-sm">
              <span className="w-3 h-3 rounded-full bg-[var(--red)] animate-pulse" />
              <span>🎤 microphone capturing audio…</span>
            </div>
          )}
          <div className="card tint-cream mb-4" style={{ minHeight: 200 }}>
            <div className="font-body text-lg sm:text-xl leading-relaxed text-[var(--ink)]">{passage.text}</div>
          </div>
          <button onClick={finish} className="btn btn-red w-full text-lg" data-testid="sprint-finish">
            ✅ I'm done — clock me!
          </button>
        </div>
      )}

      {phase === "done" && result && (
        <div className="text-center" data-testid="sprint-done">
          <div className="text-6xl mb-2 animate-pop-in">🏁</div>
          <div className="eyebrow text-[var(--orange)] mb-1">// your speed</div>
          <div className="stat-num text-6xl sm:text-7xl text-[var(--red)]">{result.wpm} <span className="text-2xl text-[var(--ink-soft)]">WPM</span></div>
          <div className="text-[var(--ink-soft)] mt-2 font-fun">{result.words} words in {result.seconds.toFixed(2)}s</div>
          <div className="mt-3 mb-4">
            <span className="pill pill-green text-base">
              {result.wpm >= 250 ? "🚀 Cosmic Speed!" : result.wpm >= 180 ? "⚡ Lightning Reader!" : result.wpm >= 120 ? "🔥 Solid pace!" : "🌱 Great start — keep on sciencing!"}
            </span>
          </div>

          {/* Recording playback + verification */}
          {recordedUrl && (
            <div className="card-flat tint-cream mb-4 text-left" data-testid="sprint-playback">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="eyebrow text-[var(--red)]">// your recording</div>
                {verifyStatus === "uploading" && (
                  <span className="pill" data-testid="verify-uploading">⏳ verifying...</span>
                )}
                {verifyStatus === "verified" && (
                  <span className="pill pill-green" data-testid="verify-ok">✅ Verified · official entry</span>
                )}
                {verifyStatus === "flagged" && (
                  <span className="pill pill-red" data-testid="verify-flag">⚠ Flagged · pending review</span>
                )}
              </div>
              {recordedType === "video" ? (
                <video src={recordedUrl} controls className="w-full rounded-xl" style={{ border: "2px solid var(--ink)" }} />
              ) : (
                <audio src={recordedUrl} controls className="w-full" />
              )}
              {verifyStatus === "flagged" && verifyFlags.length > 0 && (
                <ul className="mt-2 text-xs font-mono text-[var(--red)] list-disc pl-5">
                  {verifyFlags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
              <div className="mt-2 flex gap-2 flex-wrap items-center">
                <a href={recordedUrl} download={`speedrace-${Date.now()}.webm`} className="btn-ghost text-sm">⬇ Download</a>
                <span className="label-tag">verified entries appear on the official leaderboard</span>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center flex-wrap">
            <button onClick={reset} className="btn btn-primary" data-testid="sprint-again">🔁 Race Again</button>
            <button onClick={() => { cleanupRecording(); onClose(); }} className="btn btn-ghost">Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
